import { HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface SliderProps {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  helper?: string;
  display?: string;
  showRange?: boolean;
  formatRange?: (n: number) => string;
  helpContent?: React.ReactNode;
}

export function Slider({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix = "%",
  helper,
  display,
  showRange,
  formatRange,
  helpContent,
}: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  const fmt = formatRange ?? ((n: number) => `${n}${suffix}`);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <label className="text-sm font-medium text-foreground">{label}</label>
          {helpContent && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={`Aide : ${label}`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80 text-sm leading-relaxed">
                {helpContent}
              </PopoverContent>
            </Popover>
          )}
        </div>
        <span className="text-sm font-semibold text-primary tabular">
          {display ?? `${value}${suffix}`}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full outline-none focus:ring-2 focus:ring-primary/30 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition hover:[&::-webkit-slider-thumb]:scale-110"
        style={{
          background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${pct}%, var(--color-border) ${pct}%, var(--color-border) 100%)`,
        }}
      />
      {showRange && (
        <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular">
          <span>{fmt(min)}</span>
          <span>{fmt(max)}</span>
        </div>
      )}
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  );
}
